<template>
  <DatePicker
    v-model="internalValue"
    v-bind="$attrs"
    :format="format"
    :title-format="format"
    :value-type="valueType"
    :time-title-format="timeTitleFormat"
    :lang="lang"
    :class="{
      'datepicker--error': error,
      'datepicker--not-original': notOriginal,
      'datepicker--design-new': design === 'new'
    }"
    class="datepicker"
    :popup-class="resolvedPopupClass"
    @input-error="$emit('input-error', $event)"
    @change="$emit('input-valid')"
  />
</template>

<script>
import DatePicker from 'vue2-datepicker'

export default {
  name: 'AppDatePicker',
  components: {DatePicker},
  inheritAttrs: false,
  props: {
    value: null,
    error: {
      type: Boolean,
      default: false
    },
    format: {
      type: String,
      default: 'DD MMM YYYY'
    },
    valueType: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    timeTitleFormat: {
      type: String,
      default: 'DD MMM YYYY'
    },
    notOriginal: {
      type: Boolean,
      default: false
    },
    popupClass: {
      type: String,
      default: 'datepicker__popup'
    },
    design: {
      type: String,
      default: 'legacy',
      validator: value => ['legacy', 'new'].includes(value)
    }
  },
  data() {
    return {
      internalValue: this.value,
      lang: {
        formatLocale: {
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          firstDayOfWeek: 1
        }
      }
    }
  },
  computed: {
    resolvedPopupClass() {
      return [
        this.popupClass,
        this.design === 'new' ? 'datepicker__popup--design-new' : ''
      ].filter(Boolean).join(' ')
    }
  },
  watch: {
    value(newValue) {
      if (newValue !== this.internalValue) {
        this.internalValue = newValue
      }
    },
    internalValue(newValue) {
      if (newValue !== this.value) this.$emit('input', newValue)
    }
  },
}
</script>

<style lang="sass">
$default-color: $color-text-base
$primary-color: $color-theme

@import "vue2-datepicker/scss/index"

.datepicker
  width: 100%

  &:not(.family):not(.datepicker--design-new)
    .mx-input::placeholder
      color: #818181

  .mx-input
    height: 38px
    padding-left: 12px
    box-shadow: none
    border-radius: 6px
    border-color: $color-border
    color: $color-text-base
    font-size: 13px
    transition: border-color 0.3s

    &:focus
      border-color: #aaa

  &[readonly]
    pointer-events: none

    .mx-input
      padding: 6px 12px
      border-color: #ff9416

    .mx-icon-calendar
      display: none

.datepicker--220
  width: 220px

.datepicker--300
  width: 300px

.datepicker--center
  .mx-input
    text-align: center

.datepicker--error
  .mx-input
    border-color: $color-error

.datepicker--not-original
  .mx-input
    border-color: #ffcb9c;

.datepicker--filter
  width: 140px

.datepicker__popup
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
  font-size: 12px
  font-family: $font-family-base

  .mx-calendar
    width: 220px
    padding: 0

  .mx-calendar-header
    height: 38px
    padding: 2px 12px 0
    background-color: #f7f7f7

  .mx-calendar-content
    height: 200px
    padding: 0 6px 4px

    .mx-date-row
      .cell
        &.active
          border-radius: 50%

        &:hover
          border-radius: 50%
          background-color: #efefef

    .mx-table-date td, .mx-table-date th
      height: 28px

  .mx-time-content
    height: 200px

.datepicker__popup
  .mx-calendar-week-mode
    .mx-calendar-content .mx-date-row .cell:hover
      background-color: transparent !important
      border-radius: 0 !important
      box-shadow: none !important

    .mx-active-week .cell.active
      background: transparent !important
      color: inherit !important
      border-radius: 0 !important
      box-shadow: none !important

    .mx-calendar-content .cell.active
      background: transparent !important
      border-radius: 0 !important

.datepicker--table
  max-width: 110px

  .mx-input
    cursor: pointer
    height: auto
    border: none
    padding: 0
    font-size: 14px
    text-align: center
    background-color: transparent

  .mx-icon-calendar
    display: none

.datepicker--table-expired
  .mx-input
    padding: 5px 10px 4px
    border-radius: 20px
    color: #fff
    background-color: #e85c20
    font-weight: 600

.family.datepicker
  .mx-input
    color: #000
    font-family: 'CoreSansCR', sans-serif
    font-size: 14px
    font-weight: 400

.mx-datepicker-sidebar
  width: 120px
  overflow: hidden

  & + .mx-datepicker-content
    margin-left: 120px

.datepicker--design-new
  .mx-input
    height: $ui-control-height
    padding: 0 40px 0 $ui-space-12
    border-color: $ui-line-strong
    border-radius: $ui-control-radius
    color: $ui-text
    font-family: $ui-font-body
    font-size: $ui-font-size-body
    transition: border-color .2s ease

    &::placeholder
      color: $ui-subtle

    &:focus
      border-color: var(--ui-accent, #{$ui-blue})

  &.datepicker--error .mx-input
    border-color: $ui-red

  &[readonly] .mx-input
    border-color: $ui-line
    background-color: $ui-surface-soft
    color: $ui-muted

.datepicker__popup--design-new
  border: 1px solid $ui-line-strong
  border-radius: $ui-panel-radius
  box-shadow: none
  color: $ui-text
  font-family: $ui-font-body
  font-size: $ui-font-size-metadata
  overflow: hidden

  .mx-calendar-header
    background-color: $ui-surface-soft

  .mx-btn:hover
    border-color: var(--ui-accent, #{$ui-blue})
    color: var(--ui-accent, #{$ui-blue})

  .mx-table-date .today,
  .mx-time-item.active
    color: var(--ui-accent, #{$ui-blue})

  .cell.active
    background-color: var(--ui-accent, #{$ui-blue})
    color: #fff

  .cell:hover
    background-color: var(--ui-accent-soft, #{$ui-blue-soft})
    color: var(--ui-accent, #{$ui-blue})
</style>
